import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Collapse from '@mui/material/Collapse';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';

// Import icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import BuildIcon from '@mui/icons-material/Build';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import LogoutIcon from '@mui/icons-material/Logout';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import DoDisturbOnIcon from '@mui/icons-material/DoDisturbOn';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ChatIcon from '@mui/icons-material/Chat';
import NotificationsIcon from '@mui/icons-material/Notifications';

export default function Sidebar(props) {
  const { window } = props;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState('');
  const [openHelpers, setOpenHelpers] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const drawerWidth = {
    xs: '100%',
    sm: '280px',
    md: '19vw',
  };

  useEffect(() => {
    setSelectedItem(location.pathname);
  }, [location]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      localStorage.clear();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = [
    { name: 'Dashboard', to: '/dashboard', icon: <DashboardIcon /> },
    { name: 'Requests', to: '/requests', icon: <AssignmentIcon /> },
    { name: 'Units', to: '/units', icon: <ApartmentIcon /> },
    { name: 'Owners', to: '/owners', icon: <PeopleIcon /> },
    { name: 'Users', to: '/users', icon: <PersonIcon /> },
    { name: 'Booking Request', to: '/booking', icon: <BookOnlineIcon /> },
    { name: 'Maintenance Requests', to: '/maintenance', icon: <BuildIcon /> },
    { name: 'Cancellation Requests', to: '/cancelrequest', icon: <DoDisturbOnIcon /> },
    { name: 'Insurance Refund', to: '/insurancerefund', icon: <DoDisturbOnIcon /> },
    { name: 'Analysis', to: '/analysis', icon: <AnalyticsIcon /> },
    { name: 'Contracts', to: '/contract', icon: <PictureAsPdfIcon /> },
    { name: 'Brokers', to: '/brokers', icon: <SupervisorAccountIcon /> },
    { name: 'Broker Payment', to: '/brokerpayment', icon: <SupervisorAccountIcon /> },
    { name: 'Owners Bank Accounts', to: '/bankowners', icon: <AccountBalanceIcon /> },
    { name: 'Services Requests', to: '/servicereq', icon: <DesignServicesIcon /> },
    { name: 'Profile', to: '/profile', icon: <AccountBoxIcon /> },
    { name: 'Chats', to: '/chats', icon: <ChatIcon /> },
    { name: 'Notification', to: '/notification', icon: <NotificationsIcon /> },
    { name: 'Settings',  isDropdown: true },
    { name: 'Contact Us', to: '/contactus', icon: <ContactMailIcon /> },
    { name: 'Logout', icon: <LogoutIcon />, onClick: handleLogout },
  ];

  const handleItemClick = (path) => {
    setSelectedItem(path);
    setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: "#ffffff" }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          p: { xs: 2, md: 3 },
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
      >
        <img 
          alt="Logo" 
          src="/assets/Bask 01-02 1.png"
          style={{ 
            width: 'auto',
            height: 'auto',
            maxWidth: '80%',
            maxHeight: 64,
            objectFit: 'contain'
          }}
        />
      </Box>

      <List sx={{ flexGrow: 1, py: 1 }}>
        {menuItems.map((item, index) => (
          <React.Fragment key={index}>
            {item.isDropdown ? (
              <>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => setOpenHelpers(!openHelpers)}
                    sx={{
                      backgroundColor: selectedItem === item.to ? '#C47D60' : 'transparent',
                      '&:hover': {
                        backgroundColor: selectedItem === item.to 
                          ? '#C47D60' 
                          : 'rgba(196, 125, 96, 0.08)'
                      },
                      py: { xs: 1, md: 1.5 },
                      px: { xs: 1.5, md: 2 },
                      display: 'flex',
                      gap: 2,
                      '& .MuiListItemIcon-root': {
                        minWidth: { xs: 40, md: 48 },
                        color: selectedItem === item.to ? 'white' : 'inherit',
                      },
                      '& .MuiListItemText-primary': {
                        fontSize: { xs: '0.875rem', md: '1rem' },
                        fontWeight: selectedItem === item.to ? 600 : 400,
                        color: selectedItem === item.to ? 'white' : 'inherit',
                      }
                    }}
                  >
                    <ListItemText primary={item.name} />
                    <ListItemIcon>
                      {openHelpers ? <ExpandLess /> : <ExpandMore />}
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>

          <Collapse in={openHelpers} timeout="auto" unmountOnExit>
  <List component="div" disablePadding>
    {[
      { label: 'Banks', path: '/banks' },
      { label: 'Bank Account', path: '/banksaccount' },
 
      { label: 'Compounds', path: '/compounds' },
      { label: 'Identity Types', path: '/identitytypes' },
      { label: 'Place Type', path: '/placetype' },
      { label: 'Unit Types', path: '/unittypes' },
      { label: 'Countries', path: '/country' },
      { label: 'Cites', path: '/cites' },
      { label: 'Features', path: '/features' },
      { label: 'Services', path: '/services' },
      { label: 'Payment', path: '/Payment' },
      { label: 'Payment Methods', path: '/paymentmethods' },
      { label: 'Promo Codes', path: '/promo' },
      { label: 'Maintenance Services', path: '/maintenanceservice' },
      { label: 'Social Media', path: '/socialmedia' },
      { label: 'Setting', path: '/setting' },
      { label: 'Setting Type', path: '/settingtype' },
    ].map(({ label, path }) => (
      <ListItemButton
        key={path}
        component={Link}
        to={path}
        onClick={() => handleItemClick(path)}
        sx={{
          pl: 4,
          backgroundColor: selectedItem === path ? '#C47D60' : 'transparent',
          '&:hover': {
            backgroundColor: selectedItem === path 
              ? '#C47D60' 
              : 'rgba(196, 125, 96, 0.08)',
          },
          py: { xs: 1, md: 1.5 },
          px: { xs: 1.5, md: 2 },
          '& .MuiListItemText-primary': {
            fontSize: { xs: '0.875rem', md: '1rem' },
            fontWeight: selectedItem === path ? 600 : 400,
            color: selectedItem === path ? 'white' : 'inherit',
          }
        }}
      >
        <ListItemText primary={label} />
      </ListItemButton>
    ))}
  </List>
</Collapse>
              </>
            ) : (
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  component={item.to ? Link : 'button'}
                  to={item.to || undefined}
                  onClick={() => {
                    if (item.onClick) {
                      item.onClick();
                    } else {
                      handleItemClick(item.to);
                    }
                  }}
                  sx={{
                    width: '100%',
                    backgroundColor: selectedItem === item.to ? '#C47D60' : 'transparent',
                    '&:hover': {
                      backgroundColor: selectedItem === item.to 
                        ? '#C47D60' 
                        : 'rgba(196, 125, 96, 0.08)'
                    },
                    py: { xs: 0.75, sm: 1, md: 1.25 },
                    px: { xs: 1, sm: 1.5, md: 2 },
                    display: 'flex',
                    gap: { xs: 1, md: 2 },
                    '& .MuiListItemIcon-root': {
                      minWidth: { xs: 36, sm: 40, md: 48 },
                      color: selectedItem === item.to ? 'white' : 'inherit',
                    },
                    '& .MuiListItemText-primary': {
                      fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                      fontWeight: selectedItem === item.to ? 600 : 400,
                      color: selectedItem === item.to ? 'white' : 'inherit',
                    }
                  }}
                >
                  <ListItemText primary={item.name} />
                  <ListItemIcon>{item.icon}</ListItemIcon>
                </ListItemButton>
              </ListItem>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );

  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          display: { md: 'none' },
          backgroundColor: '#C47D60',
          boxShadow: 1,
          height:42,
          zIndex: (theme) => theme.zIndex.drawer + 5
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
              sx={{ alignSelf: 'flex-start' }}

          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            {/* <img 
              alt="Logo" 
              src="/assets/Bask 01-02 1.png"
              style={{ 
                height: 40,
                objectFit: 'contain'
              }}
            /> */}
          </Box>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth.xs, md: drawerWidth.md },
          flexShrink: { sm: 0 }
        }}
      >
        <Drawer
          container={container}
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: { xs: '85%', sm: drawerWidth.sm },
              backgroundColor: 'background.paper',
              boxShadow: 3,
              marginTop:"6.5%"
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth.md,
              backgroundColor: 'background.paper',
              boxShadow: 1,
              border: 'none'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
    </>
  );
}

Sidebar.propTypes = {
  window: PropTypes.func,
};